import { useGetOrganizations } from '@api/organisation';
import { useSetOrganization } from '@api/user-settings';
import { useMsal } from '@azure/msal-react';
import { Organisation } from '@models/organisation';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlined from '@mui/icons-material/KeyboardArrowUpOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useUserSettings } from '@providers';
import { typedMemo } from '@utils';
import { KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SettingsDialog } from './SettingsDialog';
import styles from './UserMenu.module.scss';

export const UserMenu = typedMemo(() => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Load organizations and user settings
  const { result: organizations, loading: organizationsLoading } =
    useGetOrganizations();
  const aktuelleOrganisation = useUserSettings().value.aktuelleOrganisation;
  const { mutate: setOrganization, loading: setOrganizationInProgress } =
    useSetOrganization();

  const handleUserMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUserMenuClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClick = useCallback(() => {
    setSettingsOpen(true);
    handleUserMenuClose();
  }, [handleUserMenuClose]);

  const handleLogoutClick = useCallback(() => {
    handleUserMenuClose();
    instance.logoutRedirect();
  }, [handleUserMenuClose, instance]);

  const handleOrganizationChange = useCallback(
    async (organization: Organisation) => {
      handleUserMenuClose();
      setOrganization(undefined, { id: organization.organisationId! });
    },
    [setOrganization, handleUserMenuClose],
  );

  const accountInfo = accounts?.[0];
  const familyName = accountInfo?.idTokenClaims?.family_name as string;
  const givenName = accountInfo?.idTokenClaims?.given_name as string;
  return (
    <>
      <Box
        className={styles.userSlot}
        onClick={handleUserMenuClick}
        data-testid="user-menu"
      >
        <Avatar sx={{ width: 32, height: 32 }} data-testid="user-avatar">
          {givenName?.[0]?.toLocaleUpperCase()}
          {familyName?.[0]?.toLocaleUpperCase()}
        </Avatar>
        <Stack>
          <Typography data-testid="user-name">{`${givenName ?? ''} ${familyName ?? ''}`}</Typography>
          <Typography
            variant="body2"
            component="span"
            data-testid="current-organization-display"
          >
            {setOrganizationInProgress ? (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  ml: '0 !important',
                }}
                data-testid="organization-loading"
              >
                &nbsp;
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '0.25rem',
                  }}
                />
              </Box>
            ) : (
              aktuelleOrganisation?.bezeichnung
            )}
          </Typography>
        </Stack>
        <IconButton color="inherit" size="small">
          {anchorEl ? (
            <KeyboardArrowUpOutlined />
          ) : (
            <KeyboardArrowDownOutlined />
          )}
        </IconButton>
      </Box>

      <Menu
        open={anchorEl != null}
        onClose={handleUserMenuClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableAutoFocusItem
      >
        {/* Organization Selection */}
        <MenuItem sx={{ p: 1, width: '12rem' }}>
          <Autocomplete
            disableClearable
            value={aktuelleOrganisation}
            onChange={(_, newValue) => {
              if (newValue) {
                handleOrganizationChange(newValue);
              }
            }}
            options={organizations || []}
            getOptionLabel={(option) => option.bezeichnung || ''}
            isOptionEqualToValue={(option, value) =>
              option.organisationId === value.organisationId
            }
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.stopPropagation();
              }
            }}
            loading={organizationsLoading}
            loadingText={t('common.loading')}
            disabled={organizationsLoading}
            noOptionsText={t('common.noOptions')}
            data-testid="organization-autocomplete"
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('userMenu.organization')}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                key={option.organisationId}
                data-testid={`organization-option-${option.organisationId}`}
              >
                {option.bezeichnung}
              </MenuItem>
            )}
            size="small"
            fullWidth
          />
        </MenuItem>

        <Divider />

        {/* Settings and Logout */}
        <MenuItem
          onClick={handleSettingsClick}
          data-testid="settings-menu-item"
        >
          <ListItemIcon>
            <SettingsOutlined />
          </ListItemIcon>
          <ListItemText>{t('userMenu.settings')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutOutlined />
          </ListItemIcon>
          <ListItemText data-testid="logout-button">
            {t('userMenu.logout')}
          </ListItemText>
        </MenuItem>
      </Menu>
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
});
